/*
PURPOSE: Reusable card component for displaying a user/contributor with avatar and basic info

USE CASES:
- Display users in alignment lists
- Display contributors on artist pages
- Provide clickable navigation to user profiles

DOMAIN BOUNDARIES:
- Responsible for visual presentation of user card only
- Handles navigation to user profile on click
- Does NOT handle data fetching

ASSUMPTIONS:
- User data includes userId and either displayName or firstName
- Avatar is generated from displayName/firstName using colorUtils
- Card is clickable and navigates to /u/{userId}
*/

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserColors, getUserInitials } from '../../utils/colorUtils';

interface PersonCardProps {
  userId: string;
  displayName?: string;
  firstName?: string;
  createdAt?: string;
  className?: string;
}

const PersonCard: React.FC<PersonCardProps> = ({
  userId,
  displayName,
  firstName,
  createdAt,
  className = ''
}) => {
  const navigate = useNavigate();

  // Determine display name (prioritize displayName, fallback to firstName)
  const nameToDisplay = displayName || firstName || 'User';

  // Get color scheme and initials for avatar
  const userColors = getUserColors(nameToDisplay);
  const userInitials = getUserInitials(nameToDisplay);

  // Format date if provided
  const formattedDate = createdAt
    ? new Date(createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short'
      })
    : null;

  const handleClick = () => {
    if (userId) {
      navigate(`/u/${userId}`);
    } else {
      console.warn('Person card: userId not available for navigation');
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`
        group relative bg-white rounded-lg p-4 shadow-sm hover:shadow-md
        transition-all duration-200 cursor-pointer border border-gray-100
        hover:border-coral-pink/30
        ${className}
      `}
    >
      <div className="flex flex-col items-center gap-3">
        {/* Avatar */}
        <div className="relative">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center
                       transition-transform duration-200 group-hover:scale-105"
            style={{
              background: `linear-gradient(135deg, ${userColors.primary}, ${userColors.secondary})`
            }}
          >
            <span className="text-white text-xl font-bold">
              {userInitials}
            </span>
          </div>
        </div>

        {/* User Info */}
        <div className="text-center w-full">
          <p className="font-medium text-gray-900 truncate group-hover:text-coral-pink transition-colors duration-200">
            {nameToDisplay}
          </p>
          {formattedDate && (
            <p className="text-xs text-gray-500 mt-1">
              Since {formattedDate}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonCard;
